import { useAudio } from '@utils/providers/audio-context.tsx';
import React, {
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useMemo
} from 'react';
import { Descendant } from 'slate';
import { DocumentTranscriptTextV1 } from '@utils/types/editors/transcripts/transcript.ts';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { RenderLeafProps } from 'slate-react/dist/components/editable';
import { currentWordIsSeeking } from '@utils/editors/current-work-is-seeking.ts';
import { useReadOnly, useSlate } from 'slate-react';
import { getSelectedWordFromSelection } from '@utils/editors/get-selected-word-from-selection.ts';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useTagAssignments } from '@utils/providers/tag-assignments-context.tsx';
import {
  useLeafCommentAdornments,
  LeafCommentIcon
} from '@components/editors/shared/comments/use-leaf-comment-adornments.tsx';

/* eslint-disable no-restricted-syntax -- Semantic editor highlight/playback colors must stay constant across themes for marker recognizability */

type Props = RenderLeafProps & {
  leaf: Descendant & DocumentTranscriptTextV1;
};

export const ParagraphLeaf = React.memo(
  ({ attributes, children, leaf }: PropsWithChildren & Props) => {
    const {
      setStartStopWord,
      clickedComment,
      setPopoverPosition,
      setPopoverOpen
    } = useTranscript();
    const { currentTime, setTime, linkedPlayback } = useAudio();
    const editor = useSlate();
    // #2237: in split view the source pane is read-only and playback
    // follows the translation only. Force `isCurrentWord` off and skip
    // the past/future gray coloring below so the source reads as a
    // static reference — the reader's eye stays on the target pane.
    const leafIsReadOnly = useReadOnly();
    const isCurrentWord = leafIsReadOnly
      ? false
      : currentWordIsSeeking(leaf, currentTime);
    const { theme } = useThemeContext();
    const { findAssignmentForRange } = useTagAssignments();

    const isInStartStopWordRange =
      (leaf.start ?? 0) <= (Number(clickedComment?.index_to) ?? 0) &&
      (leaf.stop ?? 0) >= (Number(clickedComment?.index_from) ?? 0);

    const tagAssignment = useMemo(() => {
      if (typeof leaf.start !== 'number' || typeof leaf.stop !== 'number') {
        return undefined;
      }
      return findAssignmentForRange(leaf.start, leaf.stop);
    }, [leaf.start, leaf.stop, findAssignmentForRange]);

    // Inline comment presence — amber for open, emerald for resolved.
    // Icon renders on the LAST word of a comment range only. Open wins
    // over resolved for both the tint and the icon so unresolved work
    // isn't drowned out by a wall of green. The lookup + icon are
    // shared with the caption editor (#2250) via a common hook.
    const commentAdornments = useLeafCommentAdornments(leaf.start, leaf.stop);
    const hasOpen = commentAdornments.underlineColor === '#f59e0b';
    const hasResolvedOnly = commentAdornments.underlineColor === '#10b981';

    const handleSelect = (e: React.MouseEvent<HTMLSpanElement>) => {
      // #2237: read-only source pane is for reading + copying only.
      // Skip our own click handling so the browser handles the native
      // text selection cleanly; no seek, no popover, no startStopWord
      // update (which would leak into the translation's toolbar).
      if (leafIsReadOnly) return;
      e.stopPropagation();

      const selectedWordElement = getSelectedWordFromSelection(editor);
      if (selectedWordElement) {
        setStartStopWord(selectedWordElement);
        if (linkedPlayback) {
          setTime(selectedWordElement.start / 1000);
        }

        const rect = e.currentTarget?.getBoundingClientRect();
        if (rect) {
          setPopoverPosition({
            x: rect.left + window.scrollX,
            y: rect.top + window.scrollY - 65
          });
          setPopoverOpen(true);
        }
      }
    };

    const spanStyle = useMemo(() => {
      // Apply 50ms shift for visual highlighting consistency
      const shiftedTime = currentTime * 1000 + 50;

      // Theme-aware colors
      const passedWordColor = theme === 'dark' ? '#b8b8b8' : '#000000'; // Lighter gray in dark mode, black in light mode
      const futureWordColor = theme === 'dark' ? '#606060' : '#707070'; // Darker gray in dark mode for contrast

      const tagColor = tagAssignment?.tag_color || '#0a8df8';

      // Comment tint — only when no higher-priority background is set.
      const commentTint = commentAdornments.tint ?? '';
      const backgroundColor = leaf.highlight
        ? '#ffeeba'
        : isInStartStopWordRange
          ? '#dbdde3'
          : leaf.mark
            ? leaf.color || '#faf600'
            : commentTint;

      // Tag underline (existing) wins the bottom edge; comment-only
      // words render a matching amber/emerald underline for discovery.
      let boxShadow: string | undefined;
      if (tagAssignment) {
        boxShadow = `inset 0 -2px 0 0 ${tagColor}`;
      } else if (commentAdornments.underlineColor) {
        boxShadow = `inset 0 -2px 0 0 ${commentAdornments.underlineColor}`;
      }

      return {
        color: (() => {
          if (leafIsReadOnly) return 'unset';
          if (isCurrentWord) return '#0a8df8';
          if (leaf.stop && leaf.stop <= shiftedTime) return passedWordColor;
          if (leaf.start && leaf.start > shiftedTime) return futureWordColor;
          return 'unset';
        })(),
        fontStyle: leaf.italic ? 'italic' : 'normal',
        backgroundColor,
        border:
          leaf.highlight && leaf.isCurrentMatch ? '1px solid #afafaf' : 'unset',
        borderRadius: leaf.highlight && leaf.isCurrentMatch ? '3px' : 'unset',
        textDecoration: `${leaf.underlined ? 'underline ' : ''}${leaf.strike ? 'line-through' : ''}`,
        ...(boxShadow ? { boxShadow } : {}),
        fontWeight: leaf.bold ? 'bold' : 'normal',
        fontSize: '18px'
      };
    }, [
      isCurrentWord,
      leaf,
      currentTime,
      isInStartStopWordRange,
      theme,
      tagAssignment,
      hasOpen,
      hasResolvedOnly,
      leafIsReadOnly
    ]);

    const handleMouseUp = useCallback(
      (e: MouseEvent<HTMLSpanElement>) => handleSelect(e),
      [handleSelect]
    );

    return (
      <span
        data-start={leaf.start}
        data-stop={leaf.stop}
        onMouseUp={handleMouseUp}
        style={spanStyle}
        {...attributes}
        {...(leaf.highlight && { 'data-cy': 'search-highlighted' })}
        {...(tagAssignment && {
          'data-tag-id': tagAssignment.tag_id,
          'data-tag-name': tagAssignment.tag_name,
          title: tagAssignment.tag_name
        })}
      >
        {children}
        <LeafCommentIcon adornments={commentAdornments} />
      </span>
    );
  }
);
